import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, Modal, ListGroup } from 'react-bootstrap';
import {
    BarChart2,
    Filter,
    FileText,
    DollarSign,
    TrendingUp,
    ShoppingBag,
    Package,
    Users,
    FileSpreadsheet,
    File,
    CheckSquare,
    Square
} from 'lucide-react';
import { getAllOrders } from '../api/orders';
import { getAllProducts } from '../api/products';
import { getAllDistributors } from '../api/partner';
import { getAllRetailers } from '../api/partner'; // Simulation, ajustez selon votre API réelle
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import useAuth from '../hooks/useAuth';
import Loading from '../components/common/Loading';
import { CSVLink } from 'react-csv'; // Et celle-ci également
import html2pdf from 'html2pdf.js'; // Alternative à jsPDF + autoTable

const ReportingPage = () => {
    const { isAdmin, isManager } = useAuth();
    const [loading, setLoading] = useState(true);
    const [reportType, setReportType] = useState('sales');
    const [timeFrame, setTimeFrame] = useState('month');
    const [startDate, setStartDate] = useState(getDefaultStartDate('month'));
    const [endDate, setEndDate] = useState(getDefaultEndDate());
    const [clientType, setClientType] = useState('ALL'); // Filtre pour type de client
    const [orderType, setOrderType] = useState('CLIENT'); // Filtre pour type de commande (CLIENT par défaut)
    const [reportData, setReportData] = useState([]);
    const [error, setError] = useState('');
    const [orders, setOrders] = useState([]);
    const [products, setProducts] = useState([]);
    const [distributors, setDistributors] = useState([]);
    const [retailers, setRetailers] = useState([]);
    const [summaryData, setSummaryData] = useState({
        totalSales: 0,
        orderCount: 0,
        averageOrderValue: 0,
        topProducts: [],
        topClients: []
    });

    // État pour le modal d'exportation
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportType, setExportType] = useState('');
    const [selectedFields, setSelectedFields] = useState({
        date: true,
        client: true,
        clientType: true,
        totalAmount: true,
        productCount: true,
        status: true
    });

    // Définir les champs disponibles pour l'exportation selon le type de rapport
    const getAvailableFields = () => {
        switch (reportType) {
            case 'sales':
                return {
                    date: 'Date',
                    client: 'Client',
                    clientType: 'Type de client',
                    totalAmount: 'Montant total',
                    productCount: 'Nombre de produits',
                    status: 'Statut'
                };
            case 'products':
                return {
                    product: 'Produit',
                    reference: 'Référence',
                    category: 'Catégorie',
                    quantitySold: 'Quantité vendue',
                    revenue: 'Revenu',
                    averagePrice: 'Prix moyen'
                };
            case 'clients':
                return {
                    client: 'Client',
                    clientType: 'Type de client',
                    orderCount: 'Nombre de commandes',
                    totalSpent: 'Montant total dépensé',
                    firstOrder: 'Première commande',
                    lastOrder: 'Dernière commande'
                };
            default:
                return {};
        }
    };

    // Colors for charts
    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

    // Helper function to get default start date based on time frame
    function getDefaultStartDate(timeFrame) {
        const date = new Date();
        switch (timeFrame) {
            case 'week':
                date.setDate(date.getDate() - 7);
                break;
            case 'month':
                date.setMonth(date.getMonth() - 1);
                break;
            case 'quarter':
                date.setMonth(date.getMonth() - 3);
                break;
            case 'year':
                date.setFullYear(date.getFullYear() - 1);
                break;
            default:
                date.setMonth(date.getMonth() - 1);
        }
        return formatDateForInput(date);
    }

    // Helper function to get default end date (today)
    function getDefaultEndDate() {
        return formatDateForInput(new Date());
    }

    // Format date for input elements
    function formatDateForInput(date) {
        return date.toISOString().split('T')[0];
    }

    // Helper to format currency
    const formatCurrency = (value, withFcfa = false) => {
        const formatted = new Intl.NumberFormat('fr-FR').format(value);
        return withFcfa ? `${formatted} Fcfa` : formatted;
    };


    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError('');

                // Fetch all the data we need
                const [ordersData, productsData, distributorsData, retailersData] = await Promise.all([
                    getAllOrders(),
                    getAllProducts(),
                    getAllDistributors(),
                    getAllRetailers() // Ajustez selon votre API réelle
                ]);

                setOrders(ordersData);
                setProducts(productsData);
                setDistributors(distributorsData);
                setRetailers(retailersData);

                // Initialize selectedFields based on available fields
                initializeSelectedFields();

                // Generate report based on current selection
                generateReport(ordersData, productsData, distributorsData, retailersData);
            } catch (err) {
                setError('Failed to load data. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Initialiser les champs sélectionnés en fonction du type de rapport
    const initializeSelectedFields = () => {
        const fields = getAvailableFields();
        const initialSelectedFields = {};

        Object.keys(fields).forEach(field => {
            initialSelectedFields[field] = true;
        });

        setSelectedFields(initialSelectedFields);
    };

    useEffect(() => {
        // Initialize selected fields when report type changes
        initializeSelectedFields();
    }, [reportType]);

    useEffect(() => {
        // Generate report when report type, time frame or client type changes
        if (orders.length > 0 && products.length > 0) {
            generateReport(orders, products, distributors, retailers);
        }
    }, [reportType, timeFrame, startDate, endDate, clientType, orderType]);

    const getClientType = (clientId) => {
        // Chercher d'abord dans les distributeurs
        const distributor = distributors.find(d => d.id === clientId);
        if (distributor) return 'DISTRIBUTEUR';

        // Ensuite dans les revendeurs
        const retailer = retailers.find(r => r.id === clientId);
        if (retailer) return 'REVENDEUR';

        return 'INCONNU';
    };

    const extractPartnerFromOrder = (order) => {
        // Vérifier si l'ordre a une structure partenaire-client
        if (order.partner && order.partner.partnerType === 'CLIENT') {
            return {
                ...order.partner,
                clientType: order.partner.type || getClientType(order.partner.id)
            };
        }

        // Si la structure est différente, essayez d'autres propriétés possibles
        if (order.client) {
            return {
                ...order.client,
                clientType: order.client.type || getClientType(order.client.id)
            };
        }

        // Fallback pour structure inconnue
        return {
            id: order.partnerId || 'unknown',
            name: 'Client Inconnu',
            clientType: getClientType(order.partnerId)
        };
    };

    const extractProductsFromOrder = (order) => {
        // Retourne un tableau d'objets produit à partir des items de commande
        if (!order.items || !Array.isArray(order.items)) {
            console.warn('Format d\'items inconnu pour la commande:', order);
            return [];
        }

        return order.items.map(item => {
            // Gestion des différentes structures possibles des items
            const productInfo = item.product || products.find(p => p.id === item.productId) || {};
            const productId = productInfo.id || item.productId;
            const productName = productInfo.name || 'Produit Inconnu';

            return {
                productId,
                productName,
                quantity: item.quantity || 0,
                price: item.price || item.unitPrice || 0,
                total: item.total || (item.quantity * item.price) || 0
            };
        });
    };

    const calculateOrderTotal = (order) => {
        // Si le total existe déjà, utilisez-le
        if (typeof order.totalAmount === 'number') {
            return order.totalAmount;
        }

        // Sinon, calculez-le à partir des items
        if (order.items && Array.isArray(order.items)) {
            return order.items.reduce((sum, item) => {
                const itemTotal = item.total || (item.quantity * (item.price || item.unitPrice || 0));
                return sum + itemTotal;
            }, 0);
        }

        return 0;
    };

    const generateReport = (ordersData, productsData, distributorsData, retailersData) => {
        // Validation des entrées
        if (!Array.isArray(ordersData) || !Array.isArray(productsData)) {
            console.error('Les données de commandes ou de produits ne sont pas au format attendu');
            return;
        }

        // Filter orders by date range and type (only CLIENT orders for sales reports)
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // Set to end of day

        // D'abord filtrer selon le type de commande (ventes ou approvisionnements)
        let filteredByTypeOrders = orderType === 'ALL'
            ? ordersData
            : ordersData.filter(order => order.type === orderType);

        // Ensuite filtrer par date
        let filteredOrders = filteredByTypeOrders.filter(order => {
            const orderDate = new Date(order.orderDate || order.createdAt);
            return orderDate >= start && orderDate <= end;
        });

        // Filtrer par type de client si nécessaire
        if (clientType !== 'ALL') {
            filteredOrders = filteredOrders.filter(order => {
                const client = extractPartnerFromOrder(order);
                return client.clientType === clientType;
            });
        }

        // Calculate summary metrics
        let totalSales = 0;
        const orderCount = filteredOrders.length;

        // Pour stocker les statistiques par produit et par client
        const productSales = {};
        const clientSales = {};

        // Traiter chaque commande
        filteredOrders.forEach(order => {
            // Extraire le client de la commande avec gestion d'erreur
            const client = extractPartnerFromOrder(order);
            const clientId = client.id;

            // Calculer le total de la commande
            const orderTotal = calculateOrderTotal(order);
            totalSales += orderTotal;

            // Enregistrer les statistiques de client
            if (!clientSales[clientId]) {
                clientSales[clientId] = {
                    id: clientId,
                    name: client.name || `Client ${clientId}`,
                    clientType: client.clientType,
                    orderCount: 0,
                    totalSpent: 0,
                    firstOrder: new Date(order.orderDate || order.createdAt),
                    lastOrder: new Date(order.orderDate || order.createdAt)
                };
            } else {
                const orderDate = new Date(order.orderDate || order.createdAt);
                if (orderDate < clientSales[clientId].firstOrder) {
                    clientSales[clientId].firstOrder = orderDate;
                }
                if (orderDate > clientSales[clientId].lastOrder) {
                    clientSales[clientId].lastOrder = orderDate;
                }
            }

            clientSales[clientId].orderCount += 1;
            clientSales[clientId].totalSpent += orderTotal;

            // Extraire et traiter les produits de la commande
            const orderProducts = extractProductsFromOrder(order);

            orderProducts.forEach(item => {
                const productId = item.productId;
                if (!productSales[productId]) {
                    // Trouver les détails complets du produit
                    const productDetails = productsData.find(p => p.id === productId) || {};

                    productSales[productId] = {
                        id: productId,
                        name: item.productName || productDetails.name || `Produit ${productId}`,
                        reference: productDetails.sku || productDetails.reference || '-',
                        category: productDetails.category || '-',
                        quantity: 0,
                        revenue: 0,
                        sales: [] // Pour calculer le prix moyen
                    };
                }
                productSales[productId].quantity += item.quantity;
                productSales[productId].revenue += item.total;
                productSales[productId].sales.push({
                    quantity: item.quantity,
                    price: item.price,
                    total: item.total
                });
            });
        });

        // Calculer le prix moyen pour chaque produit
        Object.values(productSales).forEach(product => {
            let totalQuantity = 0;
            let totalRevenue = 0;

            product.sales.forEach(sale => {
                totalQuantity += sale.quantity;
                totalRevenue += sale.total;
            });

            product.averagePrice = totalQuantity > 0 ? totalRevenue / totalQuantity : 0;
            delete product.sales; // Nettoyer les données temporaires
        });

        const averageOrderValue = orderCount > 0 ? totalSales / orderCount : 0;

        // Trier pour obtenir les meilleurs produits et clients
        const topProducts = Object.values(productSales)
            .sort((a, b) => b.revenue - a.revenue)
            .slice(0, 5);

        const topClients = Object.values(clientSales)
            .sort((a, b) => b.totalSpent - a.totalSpent)
            .slice(0, 5);

        setSummaryData({
            totalSales,
            orderCount,
            averageOrderValue,
            topProducts,
            topClients
        });

        // Generate specific report data based on type
        let data = [];

        switch (reportType) {
            case 'sales':
                // Group by appropriate time period
                data = generateSalesReport(filteredOrders, timeFrame);
                break;

            case 'products':
                // Product performance report
                data = Object.values(productSales)
                    .sort((a, b) => b.revenue - a.revenue)
                    .map(product => ({
                        name: product.name,
                        reference: product.reference,
                        category: product.category,
                        revenue: product.revenue,
                        quantity: product.quantity,
                        averagePrice: product.averagePrice
                    }));
                break;

            case 'clients':
                // Client performance report
                data = Object.values(clientSales)
                    .sort((a, b) => b.totalSpent - a.totalSpent)
                    .map(client => ({
                        name: client.name,
                        clientType: client.clientType,
                        revenue: client.totalSpent,
                        orders: client.orderCount,
                        firstOrder: client.firstOrder,
                        lastOrder: client.lastOrder
                    }));
                break;

            default:
                data = [];
        }

        setReportData(data);
    };

    const generateSalesReport = (orders, timeFrame) => {
        // Group orders by the appropriate time period
        const groupedData = {};

        orders.forEach(order => {
            const date = new Date(order.orderDate || order.createdAt);
            let key;

            switch (timeFrame) {
                case 'day':
                    key = formatDateForInput(date);
                    break;
                case 'week':
                    // Get the week number
                    const weekNumber = getWeekNumber(date);
                    key = `Semaine ${weekNumber}`;
                    break;
                case 'month':
                    key = date.toLocaleString('fr-FR', { month: 'short', year: 'numeric' });
                    break;
                case 'quarter':
                    const quarter = Math.floor(date.getMonth() / 3) + 1;
                    key = `Q${quarter} ${date.getFullYear()}`;
                    break;
                case 'year':
                    key = date.getFullYear().toString();
                    break;
                default:
                    key = formatDateForInput(date);
            }

            if (!groupedData[key]) {
                groupedData[key] = {
                    name: key,
                    sales: 0,
                    orders: 0
                };
            }

            groupedData[key].sales += calculateOrderTotal(order);
            groupedData[key].orders += 1;
        });

        // Sort by date
        return Object.values(groupedData)
            .sort((a, b) => {
                // Simple string comparison might work for most formats
                return a.name.localeCompare(b.name);
            });
    };

    // Helper to get week number
    const getWeekNumber = (date) => {
        const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
        const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
        return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    };

    const handleTimeFrameChange = (e) => {
        const newTimeFrame = e.target.value;
        setTimeFrame(newTimeFrame);
        setStartDate(getDefaultStartDate(newTimeFrame));
    };

    const toggleFieldSelection = (field) => {
        setSelectedFields({
            ...selectedFields,
            [field]: !selectedFields[field]
        });
    };

    const prepareExportData = () => {
        try {
            // Préparer les données pour l'exportation en fonction des champs sélectionnés
            const fields = getAvailableFields();
            const selectedFieldKeys = Object.keys(selectedFields).filter(key => selectedFields[key]);

            switch (reportType) {
                case 'sales':
                    // Pour le rapport des ventes, utiliser les données brutes des commandes
                    return orders
                        .filter(order => {
                            // Filtrer par type d'opération
                            if (orderType !== 'ALL' && order.type !== orderType) {
                                return false;
                            }

                            // Filtrer par date
                            const orderDate = new Date(order.orderDate || order.createdAt);
                            const start = new Date(startDate);
                            const end = new Date(endDate);
                            end.setHours(23, 59, 59, 999);

                            const isInDateRange = orderDate >= start && orderDate <= end;

                            // Filtrer par type de client si nécessaire
                            if (clientType !== 'ALL') {
                                const client = extractPartnerFromOrder(order);
                                return isInDateRange && client.clientType === clientType;
                            }

                            return isInDateRange;
                        })
                        .map(order => {
                            const client = extractPartnerFromOrder(order);
                            const orderProducts = extractProductsFromOrder(order);
                            const exportRow = {};

                            if (selectedFields.date) {
                                exportRow.date = new Date(order.orderDate || order.createdAt).toLocaleDateString();
                            }

                            if (selectedFields.client) {
                                exportRow.client = client.name || 'N/A';
                            }

                            if (selectedFields.clientType) {
                                exportRow.clientType = client.clientType || 'N/A';
                            }

                            if (selectedFields.totalAmount) {
                                exportRow.totalAmount = formatCurrency(calculateOrderTotal(order));
                            }

                            if (selectedFields.productCount) {
                                exportRow.productCount = orderProducts.length;
                            }

                            if (selectedFields.status) {
                                exportRow.status = order.status || 'N/A';
                            }

                            return exportRow;
                        });

                case 'products':
                    // Simplification pour éviter les erreurs
                    return reportData.map(product => {
                        const exportRow = {};

                        selectedFieldKeys.forEach(key => {
                            if (product[key] !== undefined) {
                                if (key === 'revenue' || key === 'averagePrice') {
                                    exportRow[fields[key]] = typeof product[key] === 'number' ?
                                        formatCurrency(product[key]) : 'N/A';
                                } else {
                                    exportRow[fields[key]] = product[key] || 'N/A';
                                }
                            } else {
                                exportRow[fields[key]] = 'N/A';
                            }
                        });

                        return exportRow;
                    });

                case 'clients':
                    // Simplification pour éviter les erreurs
                    return reportData.map(client => {
                        const exportRow = {};

                        selectedFieldKeys.forEach(key => {
                            if (client[key] !== undefined) {
                                if (key === 'totalSpent') {
                                    exportRow[fields[key]] = typeof client[key] === 'number' ?
                                        formatCurrency(client[key]) : 'N/A';
                                } else if (key === 'firstOrder' || key === 'lastOrder') {
                                    exportRow[fields[key]] = client[key] instanceof Date ?
                                        client[key].toLocaleDateString() : 'N/A';
                                } else {
                                    exportRow[fields[key]] = client[key] || 'N/A';
                                }
                            } else {
                                exportRow[fields[key]] = 'N/A';
                            }
                        });

                        return exportRow;
                    });

                default:
                    return [];
            }
        } catch (error) {
            console.error("Erreur lors de la préparation des données pour l'export:", error);
            return [];
        }
    };

    const exportToPDF = () => {
        try {
            // Vérifier si les données existent
            if (!reportData || reportData.length === 0) {
                alert("Aucune donnée à exporter");
                return;
            }

            // Récupération des colonnes sélectionnées pour le tableau
            const tableColumn = Object.keys(selectedFields)
                .filter(key => selectedFields[key])
                .map(key => getAvailableFields()[key]);

            // Préparation des données pour le tableau
            const preparedData = prepareExportData();

            // Vérifier si les données préparées existent
            if (!preparedData || preparedData.length === 0) {
                alert("Erreur: Impossible de préparer les données pour l'export");
                return;
            }

            // Déterminer le titre du rapport
            const title = reportType === 'sales' ? 'Rapport des Ventes' :
                reportType === 'products' ? 'Performance des Produits' :
                    'Performance des Clients';

            // Créer un élément pour le contenu PDF
            const pdfContent = document.createElement('div');
            pdfContent.style.padding = '15px';
            pdfContent.style.fontFamily = 'Arial, sans-serif';

            // Créer un en-tête pour le rapport
            const header = document.createElement('div');
            header.innerHTML = `
                <h1 style="text-align: center; color: #333;">${title}</h1>
                <p style="text-align: center; color: #666;">Période: ${startDate} - ${endDate}</p>
                <p style="text-align: center; color: #666;">Type d'opération: ${orderType === 'CLIENT' ? 'Ventes' :
                    orderType === 'FOURNISSEUR' ? 'Approvisionnements' :
                        'Tous'
                }</p>
                ${clientType !== 'ALL' ?
                    `<p style="text-align: center; color: #666;">Type de client: ${clientType}</p>` :
                    ''}
                <hr style="margin: 20px 0;">
            `;
            pdfContent.appendChild(header);

            // Créer un tableau HTML avec les données
            const table = document.createElement('table');
            table.style.width = '100%';
            table.style.borderCollapse = 'collapse';
            table.style.marginBottom = '20px';

            // Créer l'en-tête du tableau
            const thead = document.createElement('thead');
            const headerRow = document.createElement('tr');

            tableColumn.forEach(column => {
                const th = document.createElement('th');
                th.textContent = column;
                th.style.border = '1px solid #ddd';
                th.style.padding = '8px';
                th.style.backgroundColor = '#f2f2f2';
                headerRow.appendChild(th);
            });

            thead.appendChild(headerRow);
            table.appendChild(thead);

            // Créer le corps du tableau
            const tbody = document.createElement('tbody');

            preparedData.forEach((row, index) => {
                const tr = document.createElement('tr');
                tr.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f9f9f9';

                Object.values(row).forEach(cell => {
                    const td = document.createElement('td');
                    td.textContent = cell;
                    td.style.border = '1px solid #ddd';
                    td.style.padding = '8px';
                    tr.appendChild(td);
                });

                tbody.appendChild(tr);
            });

            table.appendChild(tbody);
            pdfContent.appendChild(table);

            // Ajouter l'élément temporairement au document
            document.body.appendChild(pdfContent);
            pdfContent.style.position = 'fixed';
            pdfContent.style.top = '-9999px';

            // Configurer les options pour html2pdf
            const options = {
                margin: 10,
                filename: `${title.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale: 2, // Améliore la qualité
                    useCORS: true
                },
                jsPDF: {
                    unit: 'mm',
                    format: 'a4',
                    orientation: 'landscape'
                }
            };

            // Générer le PDF et le télécharger
            html2pdf().from(pdfContent).set(options).save().then(() => {
                // Supprimer l'élément temporaire après téléchargement
                document.body.removeChild(pdfContent);
                setShowExportModal(false);
            });

        } catch (error) {
            console.error("Erreur lors de l'exportation PDF:", error);
            alert(`Erreur lors de la génération du PDF: ${error.message}`);
        }
    };

    const renderChart = () => {
        if (reportData.length === 0) {
            return (
                <div className="text-center py-5">
                    <BarChart2 size={48} className="text-muted mb-3" />
                    <p>Aucune donnée disponible pour la période sélectionnée</p>
                </div>
            );
        }

        switch (reportType) {
            case 'sales':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart
                            data={reportData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip
                                formatter={(value) => [formatCurrency(value), 'Ventes']}
                            />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="sales"
                                name="Ventes"
                                stroke="#8884d8"
                                activeDot={{ r: 8 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                );

            case 'products':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={reportData.slice(0, 10)} // Show top 10
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip
                                formatter={(value, name, props) => {
                                    if (name === 'revenue') {
                                        return [formatCurrency(value), 'Revenu'];
                                    } else if (name === 'quantity') {
                                        return [value, 'Quantité'];
                                    }
                                    return [value, name]; // Fallback pour toute autre série
                                }}
                            />
                            <Legend />
                            <Bar dataKey="revenue" name="Revenu" fill="#8884d8" />
                            <Bar dataKey="quantity" name="Quantité" fill="#82ca9d" />
                        </BarChart>
                    </ResponsiveContainer>
                );

            case 'clients':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <PieChart>
                            <Pie
                                data={reportData.slice(0, 5)} // Show top 5
                                cx="50%"
                                cy="50%"
                                labelLine={true}
                                outerRadius={150}
                                fill="#8884d8"
                                dataKey="revenue"
                                nameKey="name"
                                label={({ name, revenue }) => `${name}: ${formatCurrency(revenue)}`}
                            >
                                {reportData.slice(0, 5).map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                );

            default:
                return null;
        }
    };

    // Fonction pour afficher le modal d'exportation
    const showExport = (type) => {
        setExportType(type);
        setShowExportModal(true);
    };

    if (!isAdmin && !isManager) {
        return (
            <Container>
                <Alert variant="danger" className="mt-4">
                    Vous n'êtes pas autorisé à accéder à cette page.
                </Alert>
            </Container>
        );
    }

    if (loading) {
        return <Loading message="Chargement des données de reporting..." />;
    }

    // Préparer les CSV headers et data
    const csvData = prepareExportData();
    const csvHeaders = Object.keys(getAvailableFields())
        .filter(key => selectedFields[key])
        .map(key => ({
            label: getAvailableFields()[key],
            key: key
        }));

    return (
        <Container fluid>
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2 mb-4">
                <h1 className="h3 mb-0">Reporting</h1>
                <div className="d-flex flex-wrap gap-2">
                    <Button
                        variant="outline-success"
                        onClick={() => showExport('csv')}
                    >
                        <FileSpreadsheet size={16} className="me-1" />
                        CSV
                    </Button>
                    <Button
                        variant="outline-danger"
                        onClick={() => showExport('pdf')}
                    >
                        <File size={16} className="me-1" />
                        PDF
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Row className="mb-4">
                <Col xs={12} sm={6} md={3} className="mb-3">
                    <Card className="shadow-sm h-100">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                                <div className="rounded-circle bg-primary bg-opacity-25 p-2 me-2">
                                    <DollarSign size={20} className="text-primary" />
                                </div>
                                <h6 className="mb-0">Ventes Totales</h6>
                            </div>
                            <h3 className="mt-auto">{formatCurrency(summaryData.totalSales)}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                    <Card className="shadow-sm h-100">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                                <div className="rounded-circle bg-success bg-opacity-25 p-2 me-2">
                                    <FileText size={20} className="text-success" />
                                </div>
                                <h6 className="mb-0">Commandes</h6>
                            </div>
                            <h3 className="mt-auto">{summaryData.orderCount}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                    <Card className="shadow-sm h-100">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                                <div className="rounded-circle bg-info bg-opacity-25 p-2 me-2">
                                    <TrendingUp size={20} className="text-info" />
                                </div>
                                <h6 className="mb-0">Valeur Moyenne</h6>
                            </div>
                            <h3 className="mt-auto">{formatCurrency(summaryData.averageOrderValue)}</h3>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xs={12} sm={6} md={3} className="mb-3">
                    <Card className="shadow-sm h-100">
                        <Card.Body className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-3">
                                <div className="rounded-circle bg-warning bg-opacity-25 p-2 me-2">
                                    <Package size={20} className="text-warning" />
                                </div>
                                <h6 className="mb-0">Meilleur Produit</h6>
                            </div>
                            <h4 className="mt-auto">
                                {summaryData.topProducts.length > 0
                                    ? summaryData.topProducts[0].name
                                    : 'Aucun'
                                }
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Filtres du Rapport</h5>
                                <div className="d-flex align-items-center">
                                    <Filter size={16} className="me-1 text-muted" />
                                    <span className="text-muted small">Personnaliser votre rapport</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Form className="row g-3">
                                <Col xs={12} sm={6} md={3}>
                                    <Form.Group>
                                        <Form.Label>Type de Rapport</Form.Label>
                                        <Form.Select
                                            value={reportType}
                                            onChange={(e) => setReportType(e.target.value)}
                                        >
                                            <option value="sales">Ventes</option>
                                            <option value="products">Produits</option>
                                            <option value="clients">Clients</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label>Type d'Opération</Form.Label>
                                        <Form.Select
                                            value={orderType}
                                            onChange={(e) => setOrderType(e.target.value)}
                                        >
                                            <option value="CLIENT">Ventes</option>
                                            <option value="FOURNISSEUR">Approvisionnements</option>
                                            <option value="ALL">Tous</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label>Type de Client</Form.Label>
                                        <Form.Select
                                            value={clientType}
                                            onChange={(e) => setClientType(e.target.value)}
                                        >
                                            <option value="ALL">Tous</option>
                                            <option value="DISTRIBUTEUR">Distributeurs</option>
                                            <option value="REVENDEUR">Revendeurs</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label>Période</Form.Label>
                                        <Form.Select
                                            value={timeFrame}
                                            onChange={handleTimeFrameChange}
                                        >
                                            <option value="day">Jour</option>
                                            <option value="week">Semaine</option>
                                            <option value="month">Mois</option>
                                            <option value="quarter">Trimestre</option>
                                            <option value="year">Année</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label>Date de Début</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>

                                <Col xs={12} sm={6} md={2}>
                                    <Form.Group>
                                        <Form.Label>Date de Fin</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col md={12}>
                    <Card className="shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">
                                {reportType === 'sales' ? 'Rapport des Ventes' :
                                    reportType === 'products' ? 'Performance des Produits' :
                                        'Performance des Clients'}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {renderChart()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Top 5 Produits</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Produit</th>
                                        <th className="text-end">Quantité</th>
                                        <th className="text-end">Revenu</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.topProducts.length > 0 ? (
                                        summaryData.topProducts.map((product, index) => (
                                            <tr key={product.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <ShoppingBag size={16} className="me-2 text-muted" />
                                                        <span>{product.name}</span>
                                                    </div>
                                                </td>
                                                <td className="text-end">{product.quantity}</td>
                                                <td className="text-end">{formatCurrency(product.revenue)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center py-3">
                                                Aucune donnée disponible
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6} className="mb-4">
                    <Card className="shadow-sm h-100">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Top 5 Clients</h5>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table responsive className="mb-0">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Client</th>
                                        <th>Type</th>
                                        <th className="text-end">Commandes</th>
                                        <th className="text-end">Montant Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summaryData.topClients.length > 0 ? (
                                        summaryData.topClients.map((client, index) => (
                                            <tr key={client.id}>
                                                <td>{index + 1}</td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Users size={16} className="me-2 text-muted" />
                                                        <span>{client.name}</span>
                                                    </div>
                                                </td>
                                                <td>{client.clientType || 'N/A'}</td>
                                                <td className="text-end">{client.orderCount}</td>
                                                <td className="text-end">{formatCurrency(client.totalSpent)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className="text-center py-3">
                                                Aucune donnée disponible
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal pour l'exportation */}
            <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {exportType === 'csv' ? 'Exporter en CSV' : 'Exporter en PDF'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Sélectionnez les champs à inclure dans l'exportation :</p>
                    <ListGroup>
                        {Object.keys(getAvailableFields()).map(field => (
                            <ListGroup.Item
                                key={field}
                                action
                                onClick={() => toggleFieldSelection(field)}
                                className="d-flex align-items-center"
                            >
                            {selectedFields[field] ?
                                <CheckSquare size={18} className="me-2 text-primary" /> :
                                <Square size={18} className="me-2" />
                            }
                            {getAvailableFields()[field]}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowExportModal(false)}>
                        Annuler
                    </Button>
                    {exportType === 'csv' ? (
                        <CSVLink
                            data={csvData}
                            headers={csvHeaders}
                            filename={`rapport_${reportType}_${new Date().toISOString()}.csv`}
                            className="btn btn-primary"
                            onClick={() => setShowExportModal(false)}
                        >
                            <FileSpreadsheet size={16} className="me-1" />
                            Exporter CSV
                        </CSVLink>
                    ) : (
                        <Button variant="primary" onClick={exportToPDF}>
                            <File size={16} className="me-1" />
                            Exporter PDF
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ReportingPage;